import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileClock } from 'lucide-react';
import type { AuditEntry, Page } from '@simpleacs/shared';
import { api } from '../lib/api';
import { formatTimestamp } from '../lib/format';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Input,
  PageHeader,
  Pill,
  Spinner,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';

const PAGE_SIZE = 50;

function toneForAction(action: string): 'danger' | 'warn' | 'success' | 'neutral' {
  if (action.includes('failed') || action.includes('deleted') || action.includes('revoked')) {
    return 'danger';
  }
  if (action.includes('created') || action.includes('login')) return 'success';
  if (action.includes('updated') || action.includes('changed')) return 'warn';
  return 'neutral';
}

export function AuditPage() {
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);

  const auditQuery = useQuery({
    queryKey: ['audit', search, offset],
    queryFn: () =>
      api.get<Page<AuditEntry>>('/audit', { search, limit: PAGE_SIZE, offset }),
  });

  const total = auditQuery.data?.total ?? 0;

  return (
    <>
      <PageHeader
        title="Audit log"
        description="Every action taken through this platform, kept even when the actor is deleted."
        actions={
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOffset(0);
            }}
            placeholder="Filter by actor, action or target…"
            className="w-72"
          />
        }
      />

      <Card>
        {auditQuery.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : auditQuery.error ? (
          <div className="p-5">
            <ErrorNotice error={auditQuery.error} />
          </div>
        ) : (auditQuery.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            icon={<FileClock className="size-8" />}
            title="Nothing recorded yet"
            description="Actions appear here as soon as operators start working."
          />
        ) : (
          <>
            <Table>
              <THead>
                <th className="th">When</th>
                <th className="th">Actor</th>
                <th className="th">Action</th>
                <th className="th">Target</th>
                <th className="th">Detail</th>
                <th className="th">IP</th>
              </THead>
              <TBody>
                {auditQuery.data?.items.map((entry) => (
                  <TRow key={entry.id}>
                    <td className="td whitespace-nowrap text-muted">{formatTimestamp(entry.at)}</td>
                    <td className="td">{entry.actorLabel}</td>
                    <td className="td">
                      <Pill state tone={toneForAction(entry.action)}>{entry.action}</Pill>
                    </td>
                    <td className="td font-mono text-xs text-muted">
                      {entry.targetType ? `${entry.targetType}:${entry.targetId ?? '—'}` : '—'}
                    </td>
                    <td className="td max-w-sm truncate font-mono text-xs text-faint">
                      {entry.detail ? JSON.stringify(entry.detail) : '—'}
                    </td>
                    <td className="td font-mono text-xs text-faint">{entry.ip ?? '—'}</td>
                  </TRow>
                ))}
              </TBody>
            </Table>

            <div className="flex items-center justify-between border-t border-line px-5 py-3 text-sm text-muted">
              <span>
                {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  disabled={offset + PAGE_SIZE >= total}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </>
  );
}
